import React, { useState, useEffect } from 'react';
import { Search, Send } from 'lucide-react';
import { bulkSMSService } from '../../lib/services.patientinfo';
import { patientService } from '../../lib/services';
import toast from 'react-hot-toast';

const BulkSMSSend: React.FC = () => {
  const [patients, setPatients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    type: ''
  });
  
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPatients(new Set());
    } else {
      const allIds = filteredPatients.map(p => p.id);
      setSelectedPatients(new Set(allIds));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectPatient = (patientId: string) => {
    const newSelected = new Set(selectedPatients);
    if (newSelected.has(patientId)) {
      newSelected.delete(patientId);
    } else {
      newSelected.add(patientId);
    }
    setSelectedPatients(newSelected);
    setSelectAll(newSelected.size === filteredPatients.length);
  };

  const handleSendSMS = async () => {
    if (selectedPatients.size === 0) {
      toast.error('Please select at least one patient');
      return;
    }
    
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const recipients = Array.from(selectedPatients).map(patientId => {
        const patient = patients.find(p => p.id === patientId);
        return {
          patient_id: patientId,
          phone_number: patient?.phone || '',
          patient_name: patient?.name || ''
        };
      }).filter(r => r.phone_number);

      await bulkSMSService.sendBulkSMS(recipients, message, 'System');
      
      toast.success(`SMS sent to ${recipients.length} patients successfully`);
      setMessage('');
      setSelectedPatients(new Set());
      setSelectAll(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const filteredPatients = patients.filter(patient => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = !filters.search || 
      patient.name.toLowerCase().includes(searchLower) ||
      patient.unique_id?.toLowerCase().includes(searchLower) ||
      patient.phone?.includes(filters.search);
    
    const matchesFromDate = !filters.fromDate || 
      (patient.created_at && new Date(patient.created_at) >= new Date(filters.fromDate));
    
    const matchesToDate = !filters.toDate || 
      (patient.created_at && new Date(patient.created_at) <= new Date(filters.toDate));
    
    return matchesSearch && matchesFromDate && matchesToDate;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk SMS</h1>
          <p className="text-gray-600">Send SMS to multiple patients at once.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ID, Phone, Name, Search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <input
            type="date"
            placeholder="From Date"
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />

          <input
            type="date"
            placeholder="To Date"
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />

          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">--Type--</option>
            <option value="all">All Patients</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {/* Search functionality */}}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
          >
            Search
          </button>
        </div>
      </div>

      {/* Patient Selection Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    All Checked
                  </span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedPatients.has(patient.id)}
                      onChange={() => handleSelectPatient(patient.id)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-900">{patient.phone || 'No phone'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{patient.name}</div>
                    <div className="text-xs text-gray-500">{patient.unique_id}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Message Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message * ({selectedPatients.size} patients selected)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="Type your message here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <button
            onClick={handleSendSMS}
            disabled={sending || selectedPatients.size === 0 || !message.trim()}
            className="flex items-center space-x-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            <span>{sending ? 'Sending...' : 'Send SMS'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkSMSSend;
